import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error" | "invalid">("loading");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    const doUnsubscribe = async () => {
      try {
        const { error } = await supabase.functions.invoke("unsubscribe", {
          body: { token },
        });
        if (error) throw error;
        setStatus("success");
      } catch {
        setStatus("error");
      }
    };

    doUnsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Email Preferences | Hill Brothers</title>
        <meta name="description" content="Manage your email preferences for Hill Brothers Painting &amp; Contracting. Unsubscribe from communications at any time." />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content="Email Preferences | Hill Brothers" />
        <meta property="og:description" content="Manage your email preferences and unsubscribe from communications." />
        <meta property="og:url" content="https://hillbrothersnh.com/unsubscribe" />
        <link rel="canonical" href="https://hillbrothersnh.com/unsubscribe" />
      </Helmet>

      <Header />

      <main className="pt-20">
        <section className="bg-primary py-16">
          <div className="section-container">
            <Link to="/" className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
              Email Preferences
            </h1>
          </div>
        </section>

        <section className="py-20">
          <div className="section-container max-w-lg text-center">
            {status === "loading" && (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <p className="text-muted-foreground">Processing your request…</p>
              </div>
            )}

            {status === "success" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground">You've Been Unsubscribed</h2>
                <p className="text-muted-foreground">
                  You'll no longer receive email notifications from us. If you change your mind, just call us at{" "}
                  <a href="tel:+16034244608" className="text-primary underline hover:no-underline font-medium">603 424 4608</a>.
                </p>
              </div>
            )}

            {status === "error" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-destructive" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground">Something Went Wrong</h2>
                <p className="text-muted-foreground">
                  We couldn't process your unsubscribe request. Please try again or call us at{" "}
                  <a href="tel:+16034244608" className="text-primary underline hover:no-underline font-medium">603 424 4608</a>.
                </p>
              </div>
            )}

            {status === "invalid" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-muted-foreground" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground">Invalid Link</h2>
                <p className="text-muted-foreground">
                  This unsubscribe link appears to be invalid or expired. Please use the link from your email, or call us at{" "}
                  <a href="tel:+16034244608" className="text-primary underline hover:no-underline font-medium">603 424 4608</a>.
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
